import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Platform } from 'react-native';
import { Device, Organization, Store, Venue } from '@oolio-group/domain';
import { useNavigation } from '@react-navigation/native';
import { SubNavigatorName } from '../../hooks/login/useInitialScreen';
import { useDevices } from '../../hooks/organization/useDevices';
import { useVenues } from '../../hooks/organization/useVenues';
import {
  setSession,
  getDeviceSelectionStep,
  setDeviceSelectionStep,
} from '../../state/preferences';
import { sessionSubject } from '../../state/sessionObservable';
import { useCustomerDisplay } from '../../hooks/customerDisplay/useCustomerDisplay';
import { useDeviceId } from '../../hooks/deviceId/useDeviceId';
import styles from './DeviceSelection.styles';
import DeviceList from './DevicesList';
import LocationsList from './LocationsList';
import Gradient from '../../component/Gradient/Gradient';
import ButtonIcon from '../../component/Buttons/ButtonIcon';
import OrganizationsList from './OrganizationsList';
import { useOrgOptions } from '../../hooks/organization/useOrgOptions';
import { localComms } from '../../utils/localComms';
import { getAppVersion } from '../../utils/getAppVersion';
import { getPlatform } from '../../utils/getPlatform';

export enum Steps {
  ORGANIZATION = 'Organization',
  STORE = 'Store',
  DEVICE = 'Device',
  PRINTER_PROFILE = 'Printer Profile',
}

const AssignPos: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigation = useNavigation<any>();
  const { deviceId: deviceUuid } = useDeviceId();
  // Organizations are fetched from the POS GraphQL server (me.organizationPage)
  // so the picker only shows orgs the user is onboarded to on POS, matching
  // the pos-app Transition flow. SSO /userinfo would also include orgs the
  // user has SSO access to but has not been onboarded to POS — those have
  // no venues/stores/devices to select from.
  const { getOrgAsOptionsOfUser } = useOrgOptions();
  const [organizations, setOrganizations] = useState<
    { id: string; name: string }[]
  >([]);
  const [userLoading, setUserLoading] = useState(true);
  const { venues, loading: venueLoading, getVenues } = useVenues();
  const {
    devices,
    loading: deviceLoading,
    refetch: refetchDevice,
  } = useDevices();
  const {
    createCdsOnDevice,
    customerDisplay,
    loading: createCdsLoading,
  } = useCustomerDisplay();

  // Initialize step from storage if available, otherwise start at ORGANIZATION
  const [step, setStep] = useState<Steps>(() => {
    // Note: This will initialize synchronously, but we load async in useEffect
    return Steps.ORGANIZATION;
  });

  // False when the user explicitly navigated back — suppresses the single-option
  // auto-advance so we don't bounce straight forward again. Reset to true on any
  // explicit user selection.
  const shouldAutoSelectFirstOption = useRef(true);

  // Load step from storage on mount
  useEffect(() => {
    getDeviceSelectionStep()
      .then(storedStep => {
        if (storedStep && Object.values(Steps).includes(storedStep as Steps)) {
          setStep(storedStep as Steps);
        }
      })
      .catch(() => {
        // Silently handle errors - default to ORGANIZATION step
      });
  }, []);

  // Persist step changes to storage
  useEffect(() => {
    setDeviceSelectionStep(step);
  }, [step]);

  // Stream paginated org options into local state. The cancellation token
  // makes useOrgOptions abort recursion if this effect re-runs or unmounts,
  // preventing late batches from a prior fetch from leaking into state.
  useEffect(() => {
    let cancelled = false;
    setOrganizations([]);
    setUserLoading(true);
    getOrgAsOptionsOfUser(
      batch => {
        if (cancelled) return;
        setOrganizations(prev => [...prev, ...batch]);
      },
      () => !cancelled,
    )
      .catch(() => {
        // Treat as "no orgs returned" — the empty list will surface to
        // the user via the OrganizationsList empty state.
      })
      .finally(() => {
        if (!cancelled) setUserLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [getOrgAsOptionsOfUser]);

  const onSelectOrganization = useCallback(
    (organization: Pick<Organization, 'id' | 'name'>) => {
      shouldAutoSelectFirstOption.current = true;
      const oldSession = sessionSubject.value;
      // No-op session writes still produce a new reference and fan out to
      // every useSession consumer, which on a single-org account feeds the
      // auto-select effect into a render loop (INC-439). Skip the write
      // when the desired org already matches the persisted one and just
      // advance.
      if (oldSession.currentOrganization?.id !== organization.id) {
        const updatedSession = {
          ...oldSession,
          currentOrganization: organization,
        };
        sessionSubject.next(updatedSession);
        // Don't await the void setSession: awaiting it defers getVenues + setStep
        // to a microtask that a render storm can starve, leaving `step` stuck on
        // ORGANIZATION (INC-439). Persist fire-and-forget so the session update,
        // venue fetch, and step advance all land in the same synchronous batch.
        setSession(updatedSession);
      }
      // Fire the venues query with the just-picked org id explicitly, so we
      // don't depend on the auth link reading the new session in time.
      getVenues(organization.id);
      setStep(Steps.STORE);
    },
    [getVenues],
  );

  const onSelectStore = useCallback(
    (venue: Partial<Venue>, store: Store) => {
      shouldAutoSelectFirstOption.current = true;
      const oldSession = sessionSubject.value;
      const updatedSession = {
        ...oldSession,
        currentVenue: venue,
        currentStore: store,
      };
      sessionSubject.next(updatedSession);
      // Fire-and-forget the void setSession (see onSelectOrganization) so the
      // step advance isn't deferred to a starvable microtask.
      setSession(updatedSession);
      setStep(Steps.DEVICE);
      refetchDevice();
    },
    [refetchDevice],
  );

  const onSelectDevice = useCallback(
    (device: Device) => {
      // Read the latest session to ensure we preserve all previous selections
      const currentSession = sessionSubject.value;
      const updatedSession = { ...currentSession, device: device };
      sessionSubject.next(updatedSession);
      // Fire-and-forget the void setSession (see onSelectOrganization).
      setSession(updatedSession);

      localComms.setMetadata({
        name: (device.name || '') + '-CDS',
        id: device.id || '',
        os: Platform.OS,
        appType: 'CDS',
      });
      createCdsOnDevice({
        deviceUuid: deviceUuid,
        deviceId: device.id,
        name: 'Default',
        isPaired: true,
        appVersion: getAppVersion(),
        platform: getPlatform(),
      });
    },
    [createCdsOnDevice, deviceUuid],
  );

  const onBack = useCallback(() => {
    // Explicit back press — suppress the next auto-advance so a single
    // venue/store combo doesn't immediately bounce the user forward again.
    shouldAutoSelectFirstOption.current = false;
    if (step === Steps.STORE) {
      // Multi-org users go back to the org picker; single-org users have
      // nowhere meaningful to go above STORE, so fall through to Login.
      if (organizations.length > 1) {
        setStep(Steps.ORGANIZATION);
      } else {
        navigation.navigate(SubNavigatorName.Login);
      }
    } else if (step === Steps.DEVICE) {
      setStep(Steps.STORE);
    }
  }, [step, navigation, organizations.length]);

  useEffect(() => {
    if (!createCdsLoading && customerDisplay) {
      const oldSession = sessionSubject.value;
      // Clear the device selection step since we've completed the flow
      setDeviceSelectionStep(undefined);
      const updatedSession = {
        ...oldSession,
        customerDisplay,
      };
      sessionSubject.next(updatedSession);
      setSession(updatedSession);
      navigation.navigate(SubNavigatorName.Cart);
    }
  }, [createCdsLoading, customerDisplay, navigation]);

  useEffect(() => {
    if (userLoading) return;
    if (!shouldAutoSelectFirstOption.current) return;
    switch (step) {
      case Steps.ORGANIZATION: {
        if (organizations.length === 1) {
          // onSelectOrganization is now idempotent for the already-current
          // org (no session write, just getVenues + setStep) so calling it
          // unconditionally won't feed the INC-439 render loop.
          onSelectOrganization(organizations[0]);
          break;
        }
        // Only fast-path past the picker if the persisted org is still in
        // the fetched org list. A stale id (user removed from org, etc.)
        // would otherwise skip reselection and fire downstream queries
        // with an invalid org header.
        const persistedOrgId = sessionSubject.value.currentOrganization?.id;
        if (
          persistedOrgId &&
          organizations.some(org => org.id === persistedOrgId)
        ) {
          setStep(Steps.STORE);
        }
        break;
      }
      case Steps.STORE:
        if (venues?.length === 1 && venues?.[0]?.stores?.length === 1) {
          onSelectStore(venues[0], venues[0].stores[0]);
        }
        break;
      case Steps.DEVICE:
        break;
    }
  }, [
    step,
    venues,
    onSelectStore,
    onSelectOrganization,
    organizations,
    userLoading,
  ]);

  return (
    <Gradient containerStyle={styles.screenContainer}>
      <View style={styles.screen}>
        <View style={styles.modalContainer}>
          <ButtonIcon
            icon="UilArrowLeft"
            type="cancel"
            size={44}
            onPress={onBack}
            containerStyle={styles.btnBack}
          />
          {step === Steps.ORGANIZATION && (
            <OrganizationsList
              organizations={organizations}
              onSelect={onSelectOrganization}
              loading={userLoading}
            />
          )}
          {step === Steps.STORE && (
            <LocationsList
              venuesData={venues ?? []}
              onSelect={onSelectStore}
              loading={venueLoading}
            />
          )}
          {step === Steps.DEVICE && (
            <DeviceList
              onSelect={onSelectDevice}
              loading={deviceLoading || createCdsLoading}
              devices={devices ?? []}
            />
          )}
        </View>
      </View>
    </Gradient>
  );
};

export default AssignPos;
